<template>
  <div class="workflow-transition-node">
    <div class="title"> {{ title }}
      <Icon v-if="editing && isEditable" type="ios-add-circle-outline" @click="showInputField = true" />
    </div>
    <UserSelect v-if="showInputField" v-model="userId" @on-selected="handleUserSelected"></UserSelect>
    <ul class="users">
      <li v-for="user in users" :key="user.id" class="user-info" :data-user-id="user.id">
        <div class="header">
          <Avatar :src="user.avatar" />
          <span class="name">{{ user.name }}</span>
          <Icon v-if="editing && !config.readonly" type="ios-close" @click="removeUser(user.id)" />
          <span
            v-if="!config.hideStateIcons && !isTransitionEnabled && latestStateHistory && latestStateHistory.context.state">
            <Icon type="md-checkmark-circle" v-if="latestStateHistory.context.state === 'approved'" size="18"
              color="#52e039" />
            <Icon type="md-close-circle" v-if="latestStateHistory.context.state === 'rejected'" color="red" size="18" />
          </span>
          <Badge v-if="isTransitionEnabled" type="warning" :text="$t('workflow.state_badge.pending')"></Badge>
        </div>
        <div style="font-size: 80%; margin-left: 27px;" v-if="latestStateHistory">
          <span v-if="latestStateHistory.context && latestStateHistory.context.date">{{
              moment(latestStateHistory.context.date).format('YYYY-MM-DD, H:mm:ss')
          }}</span>
          <span style="display:block; line-height: 110%;"
            v-if="latestStateHistory.context && latestStateHistory.context.message">{{
                latestStateHistory.context.message
            }}</span>
        </div>
        <div v-if="config.buttons" class="workflow-btn">
          <Button v-for="btn in config.buttons" size="small" :key="btn.key" :type="btn.type"
            :loading="transitionButtonsSaving[btn.key] || false" :disabled="!isTransitionEnabled"
            @click="handleButtonClick(config, btn)" class="approval-btn">
            {{ btn.label }}
          </Button>
          <p v-if="lastRemindDate" class="py-1">Last remind date: <span style="color: #3e76f6">{{ lastRemindDate
          }}</span></p>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Vue from 'vue'
import UserSelect from '@/view/user/components/user-select'
import moment from 'moment'
import UTILS from '@/view/quote/data/utils'

export default {
  name: 'WorkflowTransitionNode',
  components: {
    UserSelect
  },
  props: {
    title: String,
    config: Object,
    workflowName: String,
    workflowModelType: String,
    workflowModelId: [Number, String],
    stateHistories: Array,
    refreshWorkflowFunc: Function,
    isTransitionEnabled: Boolean,
    editing: {
      type: Boolean,
      default: false
    },
    lastRemindDates: Object
  },
  data() {
    return {
      showInputField: false,
      users: [],
      userId: null,
      modelWorkflowUserTransitionConfig: null,
      allowMultipleUsers: false,
      transitionButtonsSaving: {}
    }
  },

  computed: {
    isEditable() {
      return !this.config.readonly && (this.allowMultipleUsers || this.users.length === 0)
    },
    latestStateHistory() {
      return this.stateHistories ? _.last(_.sortBy(this.stateHistories, ['created_at'])) : null
    },
    lastRemindDate() {
      let remindDates = this.lastRemindDates[this.config.approval_name]
      let remindDate = remindDates?.length > 0 ? remindDates[0].created_at : null
      return remindDate ? new Date(remindDate).toLocaleString() : ''
    }
  },
  mounted() {
    this.users = this.config?.users
    this.modelWorkflowUserTransitionConfig = this.config
    this.autoSendDingtalkApprovalInstance()
  },

  methods: {
    ...mapActions([
      'createWorkflowUserTransition',
      'deleteFromWorkflowUserTransitions',
      'applyWorkflowUserTransition',
      'processDingtalkRequest'
    ]),
    moment,
    handleUserSelected: function (userInfo) {
      this.showInputField = false
      if (userInfo) {
        let userName = userInfo.email ? `${userInfo.name} (${userInfo.email})` : userInfo.name
        this.users.push({ id: userInfo.id, name: userName, avatar: userInfo.avatar })

        this.createWorkflowUserTransition({
          workflowName: this.workflowName,
          modelType: this.workflowModelType,
          modelId: this.workflowModelId,
          data: { transition: this.config.transition, userId: this.userId, context: this.config.context }
        }).then(resp => {
          this.modelWorkflowUserTransitionConfig = resp.data
        })
      }
    },

    removeUser: function (uid) {
      this.users = this.users.filter(u => u.id !== uid)
      this.deleteFromWorkflowUserTransitions({ workflowName: this.workflowName, id: this.modelWorkflowUserTransitionConfig.id })
    },
    handleButtonClick(config, btn) {
      if (btn.label.toLowerCase() === 'remind') {
        this.submitDingRequest(config.approval_name)
      } else {
        let transitionContext = config.context || {}
        this.submitTransition(btn.transition, config.key, transitionContext)
      }
    },
    submitTransition(transition, btnKey, context = {}) {
      Vue.set(this.transitionButtonsSaving, btnKey, true)
      this.applyWorkflowUserTransition({
        workflowName: this.workflowName,
        transitionData: UTILS.buildWorkflowUserTransitionApplyObject(
          this.workflowModelType, this.workflowModelId, transition, context)
      }).then((resp) => {
        if (resp.data.success && resp.data.success === false) {
          this.$Message.error(resp.data.message)
        } else {
          this.refreshWorkflowFunc()
        }
      }).finally(() => {
        Vue.set(this.transitionButtonsSaving, btnKey, false)
      })
    },
    submitDingRequest(approvalName, autoSend = false) {
      let data = {
        workflowModelType: this.workflowModelType,
        workflowModelId: this.workflowModelId,
        approvalName: approvalName,
        autoSend
      }

      this.loading = true
      this.processDingtalkRequest(data).then((resp) => {
        this.$Notice.success({
          title: "Dingtalk request successful",
          desc: resp.message,
          duration: 10,
          closable: true
        })
      }).finally(() => {
        this.loading = false
        this.$emit('dingtalk-request-completed')
      })
    },
    autoSendDingtalkApprovalInstance() {
      let autoSendAllowed = false
      switch (this.config.approval_name) {
        case 'cost_approval':
          autoSendAllowed = true
          break
        default:
          break
      }
      if (this.isTransitionEnabled && autoSendAllowed) {
        this.autoSendDingtalkApprovalRequest({
          wworkflowTransitionId: this.modelWorkflowUserTransitionConfig.id,
          approvalName: this.config.approval_name
        }).then((resp) => {
          // silenec message
        })
      }
    }
  }
}
</script>
<style lang="less">
.workflow-transition-node {
  li.user-info {
    background-color: #e9e9e95e;
    padding-left: 1.05rem;
    list-style: none;
    line-height: 24px;
    padding: 0.5rem 1.05rem;

    .name {
      color: #3e76f6;
      margin-right: 0.35rem;
    }

    .ivu-avatar {
      margin-right: 3px;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      vertical-align: top;
    }

    .workflow-btns {
      margin-left: 23px;

      button {
        margin: 0.3rem;
      }
    }

    .approval-btn {
      margin-right: .5rem;
    }

    // .remind-btn{
    //   margin-right: .5rem;
    //   background:none;
    //   border: 1.5px solid green;
    //   color: green;
    // }
    // .reject-btn{
    //   margin-right: .5rem;
    //   background:none;
    //   border: 2px solid gray;
    // }

  }

}
</style>
